import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { Input } from '../input';
import { InputGroupInput } from '../input-group';
import { Textarea } from '../textarea';
import { Checkbox } from '../checkbox';
import { Switch } from '../switch';
import { RadioGroup, RadioGroupItem } from '../radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';
import { MultiSelect, type MultiSelectOption } from '../multi-select';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { Button } from '../button';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandItem,
} from '../command';
import { Label } from '../label';
import { useFormFieldContext } from './form-field-context';
import { useFieldAria } from './use-field-aria';
import { deriveFieldAutoComplete } from './derive-auto-complete';

/** A selectable option used by Select / RadioGroup controls. */
export interface FieldOption {
  label: string;
  value: string;
  disabled?: boolean;
}

/* ------------------------------- Input -------------------------------- */

export function FormFieldInput({
  autoComplete,
  type,
  ...props
}: Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'name'>) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const resolvedAutoComplete = autoComplete ?? deriveFieldAutoComplete(field.name, type);
  return (
    <Input
      {...aria}
      type={type}
      autoComplete={resolvedAutoComplete}
      name={field.name}
      value={(field.value as string | number | undefined) ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      ref={field.ref}
      {...props}
    />
  );
}

/* ----------------------------- GroupInput ----------------------------- */

export function FormFieldGroupInput({
  autoComplete,
  type,
  ...props
}: Omit<React.ComponentProps<typeof InputGroupInput>, 'value' | 'onChange' | 'name'>) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const resolvedAutoComplete = autoComplete ?? deriveFieldAutoComplete(field.name, type);
  return (
    <InputGroupInput
      {...aria}
      type={type}
      autoComplete={resolvedAutoComplete}
      name={field.name}
      value={(field.value as string | number | undefined) ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      ref={field.ref}
      {...props}
    />
  );
}

/* ------------------------------- Number ------------------------------- */

export interface FormFieldNumberProps extends Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange' | 'name' | 'type'
> {
  /** Smallest accepted value. Enforced on blur, not per keystroke. */
  min?: number;
  /** Largest accepted value. A typed number above it is clamped down. */
  max?: number;
  /**
   * Digits allowed after the decimal point. `0` (the default) makes the field
   * integer-only and drops the separator entirely.
   */
  decimals?: number;
  /**
   * Allow a leading minus sign. Off by default — bounded quantities (search
   * time, prep time) are never negative. Opt in for fields with a genuinely
   * unbounded range such as min balance.
   */
  allowNegative?: boolean;
  /**
   * Trailing unit rendered inside the pill (`мин`, `%`) — the same affordance
   * `FormField.Price` offers for a currency, so a bounded quantity can state
   * what it counts without a second label.
   */
  suffix?: React.ReactNode;
}

/**
 * Bounded numeric input. Unlike `<input type="number">` — whose `min`/`max`
 * only gate the stepper arrows and native form validation — this REFUSES the
 * out-of-range or over-precise value as it is entered, so the box can never
 * hold something the schema will later reject.
 *
 * Kept as text with `inputMode="numeric"`: a real `type="number"` accepts `e`,
 * `+` and `-` (exponent notation), reports them as an empty `value`, and hides
 * the difference between "cleared" and "invalid".
 *
 * Clamping splits across two events on purpose. Per keystroke only the SHAPE is
 * enforced (digits, one separator, the decimal budget) plus the `max` ceiling;
 * `min` is applied on blur, because raising a half-typed "1" to a minimum of 10
 * while the merchant is still reaching for the second digit fights the typing.
 */
export function FormFieldNumber({
  min,
  max,
  decimals = 0,
  allowNegative = false,
  suffix,
  ...props
}: FormFieldNumberProps) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();

  const display =
    field.value === undefined || field.value === null ? '' : String(field.value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    if (raw === '') {
      // Empty is a value the merchant can be in the middle of, not a zero.
      // An emptied box stores `null`, not `undefined`, so RHF's Controller does
      // not treat it as missing and snap back to the field's default value.
      field.onChange(null);
      return;
    }

    // Normalize a comma separator, keep digits and at most one point. The
    // minus survives only as a single leading sign behind `allowNegative` —
    // every other usage stays non-negative exactly as before.
    const negative = allowNegative && raw.trimStart().startsWith('-');
    let next = raw.replace(/,/g, '.').replace(/[^\d.]/g, '');
    if (negative) next = `-${next}`;
    const [whole = '', ...rest] = next.split('.');
    next = rest.length > 0 ? `${whole}.${rest.join('')}` : whole;

    if (decimals <= 0) {
      next = next.split('.')[0] ?? '';
    } else {
      const [head = '', tail] = next.split('.');
      next = tail === undefined ? head : `${head}.${tail.slice(0, decimals)}`;
    }
    if (next === '' || next === '.') {
      field.onChange(null);
      return;
    }

    // A lone minus or trailing separator is a legitimate mid-typing state
    // ("-", "12."), so it is held as text rather than parsed into a number
    // that would erase it. Blur resolves "-" back to empty (below).
    if (next === '-' || next.endsWith('.')) {
      field.onChange(next);
      return;
    }

    const parsed = Number(next);
    if (Number.isNaN(parsed)) return;
    field.onChange(max !== undefined && parsed > max ? max : parsed);
  };

  const handleBlur = () => {
    // A minus with no digits never becomes a value — it is an abandoned
    // negative, same as a cleared box.
    if (field.value === '-') {
      field.onChange(null);
      field.onBlur();
      return;
    }
    const parsed = Number(field.value);
    if (field.value !== undefined && field.value !== null && !Number.isNaN(parsed)) {
      const clamped =
        min !== undefined && parsed < min
          ? min
          : max !== undefined && parsed > max
            ? max
            : parsed;
      if (clamped !== field.value) field.onChange(clamped);
    }
    field.onBlur();
  };

  return (
    <Input
      {...aria}
      type="text"
      autoComplete={props.autoComplete ?? 'off'}
      inputMode={decimals > 0 ? 'decimal' : 'numeric'}
      name={field.name}
      value={display}
      onChange={handleChange}
      onBlur={handleBlur}
      ref={field.ref}
      {...(suffix !== undefined
        ? {
            endIcon: (
              <span className="text-sm font-medium tracking-[-0.28px] text-muted-foreground">
                {suffix}
              </span>
            ),
          }
        : {})}
      {...props}
    />
  );
}

/* ------------------------------ TextArea ------------------------------ */

export function FormFieldTextArea({
  autoComplete = 'off',
  ...props
}: Omit<React.ComponentProps<typeof Textarea>, 'value' | 'onChange' | 'name'>) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  return (
    <Textarea
      {...aria}
      autoComplete={autoComplete}
      name={field.name}
      value={(field.value as string | undefined) ?? ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      ref={field.ref}
      {...props}
    />
  );
}

/* ------------------------------- Select ------------------------------- */

export function FormFieldSelect({
  options,
  placeholder,
  className,
  disabled,
  onValueChange,
  searchable = false,
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
}: {
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  options: FieldOption[];
  placeholder?: string;
  className?: string;
  /** Disables the trigger while a mutation is in flight. */
  disabled?: boolean;
  /** Runs in addition to the default RHF `field.onChange`, e.g. to cascade a dependent field. */
  onValueChange?: (value: string) => void;
}) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const [open, setOpen] = React.useState(false);
  const selectValue = (value: string) => {
    field.onChange(value);
    onValueChange?.(value);
  };
  if (searchable) {
    const selected = options.find((option) => option.value === field.value);
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            {...aria}
            ref={field.ref}
            type="button"
            role="combobox"
            aria-expanded={open}
            variant="outline"
            disabled={disabled ?? field.disabled}
            onBlur={field.onBlur}
            className={cn(
              'w-full min-w-0 justify-between rounded-[var(--field-radius)] font-normal',
              className,
            )}
          >
            <span className={cn('truncate', !selected && 'text-muted-foreground')}>
              {selected?.label ?? placeholder}
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          portal={false}
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-1"
        >
          <Command>
            <CommandInput
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
            <CommandList>
              <CommandEmpty>{emptyText}</CommandEmpty>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  keywords={[option.label]}
                  disabled={option.disabled ?? false}
                  onSelect={() => {
                    selectValue(option.value);
                    setOpen(false);
                  }}
                >
                  <CheckIcon
                    className={cn(
                      'size-4 shrink-0',
                      selected?.value !== option.value && 'invisible',
                    )}
                  />
                  <span>{option.label}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Select
      value={(field.value as string | undefined) ?? ''}
      onValueChange={(value) => {
        field.onChange(value);
        onValueChange?.(value);
      }}
      {...(disabled !== undefined ? { disabled } : {})}
    >
      <SelectTrigger
        id={aria.id}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        className={cn('w-full', className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            {...(option.disabled ? { disabled: true } : {})}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/* ---------------------------- MultiSelect ----------------------------- */

export function FormFieldMultiSelect({
  options,
  placeholder,
  searchPlaceholder,
  searchable,
  emptyText,
  className,
  portal,
  panel,
  selectAllLabel,
  summaryLabel,
  removableChips,
}: {
  options: MultiSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Forwarded to `MultiSelect`; hides the dropdown search when `false`. */
  searchable?: boolean;
  emptyText?: string;
  className?: string;
  /** Forwarded to `MultiSelect`; defaults to in-place (needed inside a modal). */
  portal?: boolean;
  /** Open the options as a card docked beside the modal instead of a dropdown. */
  panel?: boolean;
  /** Header toggle label for the docked panel and the dropdown's strip. */
  selectAllLabel?: string;
  /** Collapse the trigger into a count line instead of per-option chips. */
  summaryLabel?: (selected: number, total: number) => string;
  /** Render per-chip remove buttons; `false` removes only via the dropdown checkboxes. */
  removableChips?: boolean;
}) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  const value = Array.isArray(field.value) ? (field.value as string[]) : [];
  return (
    <MultiSelect
      id={aria.id}
      aria-describedby={aria['aria-describedby']}
      aria-invalid={aria['aria-invalid']}
      value={value}
      onChange={field.onChange}
      onBlur={field.onBlur}
      options={options}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(searchPlaceholder !== undefined ? { searchPlaceholder } : {})}
      {...(searchable !== undefined ? { searchable } : {})}
      {...(emptyText !== undefined ? { emptyText } : {})}
      {...(className !== undefined ? { className } : {})}
      {...(portal !== undefined ? { portal } : {})}
      {...(panel !== undefined ? { panel } : {})}
      {...(selectAllLabel !== undefined ? { selectAllLabel } : {})}
      {...(summaryLabel !== undefined ? { summaryLabel } : {})}
      {...(removableChips !== undefined ? { removableChips } : {})}
    />
  );
}

/* ----------------------------- RadioGroup ----------------------------- */

export function FormFieldRadioGroup({
  options,
  className,
  labelClassName,
}: {
  options: FieldOption[];
  className?: string;
  /** Styling passthrough for each option label (size, color). */
  labelClassName?: string;
}) {
  const { field, formItemId } = useFormFieldContext();
  const aria = useFieldAria();
  return (
    <RadioGroup
      value={(field.value as string | undefined) ?? ''}
      onValueChange={field.onChange}
      aria-describedby={aria['aria-describedby']}
      aria-invalid={aria['aria-invalid']}
      className={cn('flex flex-wrap items-center gap-8', className)}
    >
      {options.map((option) => {
        const itemId = `${formItemId}-${option.value}`;
        return (
          <div key={option.value} className="flex items-center gap-3">
            <RadioGroupItem
              id={itemId}
              value={option.value}
              {...(option.disabled ? { disabled: true } : {})}
            />
            <Label
              htmlFor={itemId}
              // Type inherits `Label`'s `--form-label-*` sizing handles — only
              // the pointer + dark tint are stated here so the dial-board owns
              // the type ramp.
              className={cn('cursor-pointer dark:text-muted-foreground', labelClassName)}
            >
              {option.label}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
}

/* ------------------------------ Checkbox ------------------------------ */

export function FormFieldCheckbox({
  label,
  className,
  checkboxClassName,
  labelClassName,
}: {
  label?: React.ReactNode;
  className?: string;
  /** Styling passthrough for the checkbox box itself (size, radius, border). */
  checkboxClassName?: string;
  /** Styling passthrough for the label (size, color). */
  labelClassName?: string;
}) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Checkbox
        id={aria.id}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        checked={Boolean(field.value)}
        onCheckedChange={(checked) => field.onChange(checked === true)}
        onBlur={field.onBlur}
        ref={field.ref}
        {...(checkboxClassName ? { className: checkboxClassName } : {})}
      />
      {label ? (
        <Label
          htmlFor={aria.id}
          // Type inherits `Label`'s `--form-label-*` sizing handles.
          className={cn('cursor-pointer dark:text-muted-foreground', labelClassName)}
        >
          {label}
        </Label>
      ) : null}
    </div>
  );
}

/* ------------------------------- Switch ------------------------------- */

export function FormFieldSwitch({
  label,
  className,
}: {
  label?: React.ReactNode;
  className?: string;
}) {
  const { field } = useFormFieldContext();
  const aria = useFieldAria();
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <Switch
        id={aria.id}
        aria-describedby={aria['aria-describedby']}
        aria-invalid={aria['aria-invalid']}
        checked={Boolean(field.value)}
        onCheckedChange={(checked) => field.onChange(checked)}
        ref={field.ref}
      />
      {label ? (
        <Label
          htmlFor={aria.id}
          // Type inherits `Label`'s `--form-label-*` sizing handles.
          className="cursor-pointer peer-disabled:text-switch-label-fg-disabled dark:text-muted-foreground"
        >
          {label}
        </Label>
      ) : null}
    </div>
  );
}
